import { arrayMove } from '@dnd-kit/sortable';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { FileAcceptType, OnCropCompleteArgs, UploadType, useImageCrop } from '../../../../../hooks';
import {
  ImageBlockAlignment,
  ImageBlockData,
  ImageBlockDataItem,
  ImageBlockSize,
} from '../../../../../models';
import { getCssVar, isFloatedImageBlock, isRemoteFileUrl } from '../../../../../utils';
import { CircularLoader } from '../../../../CircularLoader';
import { IconLabel } from '../../../../IconLabel';
import { ImageCropModal, isCroppableImageSource } from '../../../../ImageCropModal';
import { InputField } from '../../../../InputField';
import { ListOpenDirection, Select } from '../../../../Select';
import { ReorderArgs, SortableItem, SortableList, SortableMode } from '../../../../SortableList';
import { CloseWrapper, MediaBlockUpload, useNodeAttributeValue } from '../../helpers';

import classNames from 'classnames';
import classes from './ImageNodeView.module.scss';

const UPLOAD_LIMIT = 10;

export const ImageNodeView: FunctionComponent<NodeViewProps> = ({
  editor,
  node,
  getPos,
  updateAttributes,
}) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'richTextEditor' });

  const { value: items, changeHandler: setItems } = useNodeAttributeValue({
    value: node.attrs.items as ImageBlockDataItem[],
    onChange: (value) => updateAttributes({ items: value }),
  });

  const pastedFiles = node.attrs.pastedFiles as File[] | undefined;

  const onCropComplete = useCallback(
    ({ previousImageId, image }: OnCropCompleteArgs) => {
      updateAttributes({
        items: items.map((item) =>
          item.image?.id === previousImageId ? { ...item, image } : item
        ),
      });
    },
    [items, updateAttributes]
  );

  const { fileToCrop, cropImageId, isCropUploading, openCrop, onCrop, onCropClose } = useImageCrop({
    onCropComplete,
  });

  const [loading, setLoading] = useState<boolean>(false);

  const [selectId, setSelectId] = useState<number>(0);

  const { alignment, size } = node.attrs as ImageBlockData;

  const isFloated = isFloatedImageBlock({ items, alignment });

  const alignmentItems = useMemo(
    () =>
      Object.values(ImageBlockAlignment).map((alignment) => ({
        id: alignment,
        title: ctxT(`tools-image-align-${alignment}`),
      })),
    [ctxT]
  );

  const sizeItems = useMemo(
    () =>
      Object.values(ImageBlockSize).map((size) => ({
        id: size,
        title: ctxT(`tools-image-size-${size}`),
      })),
    [ctxT]
  );

  const isDraggable = useMemo(() => Boolean(!loading && items.length > 1), [items.length, loading]);

  const caption = useMemo(
    () => items.find((item) => item.image?.id === selectId)?.caption ?? '',
    [items, selectId]
  );

  const moveHandler = useCallback(
    ({ activeId, overId }: ReorderArgs) => {
      const activeIndex = items.findIndex(({ image }) => image?.id === activeId);

      const overIndex = items.findIndex(({ image }) => image?.id === overId);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      setItems(arrayMove(items, activeIndex, overIndex));
    },
    [items, setItems]
  );

  const removePreview = useCallback(
    (index: number) => {
      const itemsCopy = [...items];

      const { image } = itemsCopy.splice(index, 1)[0];

      if (selectId === image?.id) {
        setSelectId(0);
      }

      updateAttributes({ items: itemsCopy });
    },
    [items, selectId, updateAttributes]
  );

  const setCaption = useCallback(
    (caption: string) => {
      setItems(items.map((item) => (item.image?.id === selectId ? { ...item, caption } : item)));
    },
    [items, selectId, setItems]
  );

  const preview = useMemo(() => {
    return (
      <div className={classes['gallery__preview-wrapper']}>
        <SortableList
          mode={SortableMode.GRID}
          items={items.map(({ image }) => image?.id ?? 0)}
          onReorder={moveHandler}
          disabled={!isDraggable}
        >
          {items.map(({ image, caption }, index) => {
            const { id = 0, url } = { ...image };

            const canOpenImage = isRemoteFileUrl(url);

            const canCropImage = Boolean(url && isCroppableImageSource(url));

            const isCropping = isCropUploading && id === cropImageId;

            return (
              <SortableItem
                key={id}
                id={id}
                disabled={isCropping}
                className={classNames(classes['gallery__preview'], {
                  [classes['gallery__preview--loading']]: loading || isCropping,
                  [classes['gallery__preview--move']]: isDraggable,
                })}
              >
                <div
                  className={classes['gallery__preview-img']}
                  style={{ backgroundImage: `url(${url})` }}
                ></div>

                {isCropping && (
                  <div className={classes['gallery__preview-loader']}>
                    <CircularLoader sizeRem={2} />
                  </div>
                )}

                {(canOpenImage || canCropImage) && (
                  <div
                    className={classNames(
                      classes['gallery__preview-actions'],
                      classes['gallery__preview-actions--left']
                    )}
                  >
                    {canOpenImage && (
                      <IconLabel
                        className={classes['gallery__preview-actions-link']}
                        iconId={'arrow-rounded-right-up-box'}
                        iconSize={18}
                        ariaLabel={t('common.open-in-new-tab')}
                        color={getCssVar('--base-link-text-color')}
                        hoverColor={getCssVar('--base-link-text-hover-color')}
                        onClick={() => window.open(url, '_blank', 'noopener, noreferrer')}
                      />
                    )}

                    {canCropImage && image && (
                      <IconLabel
                        className={classes['gallery__preview-actions-crop']}
                        iconId={'crop'}
                        iconSize={18}
                        ariaLabel={t('imageCrop.title')}
                        onClick={() => openCrop(image)}
                      />
                    )}
                  </div>
                )}

                <div className={classes['gallery__preview-actions']}>
                  <span
                    className={classNames(classes['gallery__preview-actions-settings'], {
                      [classes['gallery__preview-actions-settings--active']]: id === selectId,
                    })}
                    onClick={() => setSelectId(selectId === id ? 0 : id)}
                  >
                    <IconLabel
                      iconId={caption || isFloated ? 'pencil' : 'dots-menu'}
                      iconSize={30}
                    />
                  </span>
                  <span
                    className={classes['gallery__preview-actions-remove']}
                    onClick={() => removePreview(index)}
                  ></span>
                </div>
              </SortableItem>
            );
          })}
        </SortableList>
      </div>
    );
  }, [
    cropImageId,
    isCropUploading,
    isDraggable,
    isFloated,
    items,
    loading,
    openCrop,
    removePreview,
    selectId,
    t,
    moveHandler,
  ]);

  const content = useMemo(() => {
    return (
      <div className={classes['gallery-wrapper']}>
        {Boolean(items.length < UPLOAD_LIMIT) && (
          <MediaBlockUpload
            items={items}
            setItems={(items) => updateAttributes({ items })}
            pastedFiles={pastedFiles}
            loading={loading}
            setLoading={setLoading}
            fileAcceptType={FileAcceptType.IMAGE}
            uploadType={UploadType.IMAGE}
            uploadLimit={UPLOAD_LIMIT}
            multiple
          />
        )}
        {Boolean(items.length) && (
          <div className={classes['gallery']}>
            {preview}
            {Boolean(selectId) && (
              <>
                <InputField
                  value={caption}
                  onChange={({ target }) => setCaption(target.value)}
                  label={ctxT('tools-image-caption')}
                />
                {items.length === 1 && (
                  <div
                    className={classNames(classes['gallery__layout'], {
                      [classes['gallery__layout--mobile']]: isMobileOnly,
                    })}
                  >
                    <Select
                      selectedItemId={alignment}
                      items={alignmentItems}
                      listOpenDirection={ListOpenDirection.top}
                      onChange={(alignment) => updateAttributes({ alignment })}
                    />
                    {isFloated && (
                      <Select
                        selectedItemId={size}
                        items={sizeItems}
                        listOpenDirection={ListOpenDirection.top}
                        onChange={(size) => updateAttributes({ size })}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }, [
    alignment,
    alignmentItems,
    caption,
    ctxT,
    isFloated,
    items,
    loading,
    pastedFiles,
    preview,
    selectId,
    setCaption,
    size,
    sizeItems,
    updateAttributes,
  ]);

  return (
    <NodeViewWrapper as={'div'}>
      <CloseWrapper
        editor={editor}
        node={node}
        getPos={getPos}
        loading={Boolean(loading || (pastedFiles?.length && !items.length))}
      >
        {content}
      </CloseWrapper>

      {fileToCrop && (
        <ImageCropModal
          file={fileToCrop}
          title={t('imageCrop.title')}
          onCrop={onCrop}
          onClose={onCropClose}
          root={'modal-editor-root'}
        />
      )}
    </NodeViewWrapper>
  );
};
